import type {
  MultipleChoiceQuestion,
  SurveyHandler,
  SurveyError,
} from 'src/types/survey';
// @ts-expect-error - TS2614 - Module '"src/api-parsers/events"' has no exported member 'Answer'. Did you mean to use 'import Answer from "src/api-parsers/events"' instead?
import type {Answer} from 'src/api-parsers/events';
import type {AttributeMetaData} from 'src/types/writeback';

import * as React from 'react';

import {classify} from 'src/utils';

import Select from 'src/components/lib/select/select';
import TokenListInput from 'src/components/lib/token-list-input';
import RadioButtons from 'src/components/lib/radio-buttons/radio-buttons';
import Checkbox from 'src/components/lib/checkbox';

import css from './multiple-choice.css';


const buttonClass = css.submitButton.split(' ')[0];
//following pattern of other survey modules

type Props = {
  data: MultipleChoiceQuestion;
  response?: Answer;
  handleNext: SurveyHandler;
  brandColor: string;
  buttonColor: string;
  currentError: SurveyError;
  atsFieldWritebackEnabled: boolean;
  fetching: boolean;
  audienceVariables: {
    [key: string]: Array<string> | string | null | undefined;
  };
  writebackFieldAttributes: AttributeMetaData;
};

const MultipleChoiceSurveyQuestion = ({
  data,
  response,
  atsFieldWritebackEnabled,
  audienceVariables,
  buttonColor,
  currentError,
  handleNext,
  fetching,
  writebackFieldAttributes,
}: Props): React.ReactElement<React.ComponentProps<'div'>> => {
  const writebackPrefillEnabled =
    atsFieldWritebackEnabled &&
    data.writeback?.show_current_value &&
    data.writeback?.attribute_name;
  const writebackPrefill =
    writebackPrefillEnabled &&
    // @ts-expect-error - TS18048 - 'data.writeback' is possibly 'undefined'. | TS2538 - Type 'undefined' cannot be used as an index type.
    audienceVariables?.[data.writeback.attribute_name];

  const [selected, setSelected] = React.useState([]);

  React.useEffect(() => {
    const prefillValue = response?.moduleValue || writebackPrefill;
    // @ts-expect-error - TS2339 - Property 'allow_select_multiple' does not exist on type 'MultipleChoiceQuestion'.
    if (data.allow_select_multiple) {
      const prefill =
        prefillValue && Array.isArray(prefillValue)
          ? prefillValue
              .map((choice) => {
                const isValidPrefill = data.choices.find(
                  ({value}) => choice === value,
                );

                return choice && isValidPrefill ? choice : null;
              })
              .filter(Boolean)
          : [];
      // @ts-expect-error - TS2345 - Argument of type 'any[]' is not assignable to parameter of type 'SetStateAction<never[]>'.
      setSelected(prefill);
    } else {
      const isValidPrefill = data.choices.find(
        ({value}) => prefillValue === value,
      );
      // @ts-expect-error - TS2345 - Argument of type 'any[]' is not assignable to parameter of type 'SetStateAction<never[]>'.
      setSelected(isValidPrefill ? [prefillValue] : []);
    }
  }, [response?.moduleValue, writebackPrefill]);

  const showChoiceDropdown = data?.choices.length > 6;

  const choicesText = data.writeback?.choices_text;
  const mappedChoices = data.choices.map(({id, value}) => ({
    id,
    value,
    label: choicesText ? choicesText[value] : value,
  }));

  if (showChoiceDropdown) {
    mappedChoices.sort((a, b) =>
      a.label.toLowerCase().localeCompare(b.label.toLowerCase()),
    );
  }

  const allowSelectMultiple =
    // @ts-expect-error - TS2339 - Property 'allow_select_multiple' does not exist on type 'MultipleChoiceQuestion'.
    data.allow_select_multiple &&
    // @ts-expect-error - TS2339 - Property 'multi_select_limit' does not exist on type 'MultipleChoiceQuestion'. | TS2339 - Property 'multi_select_limit' does not exist on type 'MultipleChoiceQuestion'.
    (!data.multi_select_limit || data.multi_select_limit > 1);

  const isMultiSelectVariable =
    data.writeback && writebackFieldAttributes?.allow_select_multiple;

  const showCheckboxes = !showChoiceDropdown && allowSelectMultiple;
  const showRadioButtons = !showChoiceDropdown && !allowSelectMultiple;

  const allowSubmit = Boolean(selected.length) || !data.required;

  return (
    <div className={css.multipleChoice}>
      <p className={css.multipleChoiceText}>
        {data.question}
        {data.required && ' *'}
      </p>
      {currentError && (
        <p className={css.multipleChoiceSurveyError}>{currentError.message}</p>
      )}
      {showChoiceDropdown && (
        <div className={css.multipleChoice}>
          {allowSelectMultiple ? (
            // @ts-expect-error - TS2769 - No overload matches this call.
            <TokenListInput
              options={mappedChoices}
              values={mappedChoices.filter(({value}) =>
                // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'never'.
                selected.includes(value),
              )}
              disabled={fetching}
              placeholder="Select all that apply"
              allowArbitraryValues={false}
              openOnFocus
              verticalStretch
              onChange={(selectedChoices) => {
                // @ts-expect-error - TS2345 - Argument of type 'unknown[]' is not assignable to parameter of type 'SetStateAction<never[]>'.
                setSelected(selectedChoices.map(({value}) => value));
              }}
              className={css.choiceInput}
              // @ts-expect-error - TS2339 - Property 'multi_select_limit' does not exist on type 'MultipleChoiceQuestion'.
              limit={data.multi_select_limit ?? undefined}
            />
          ) : (
            <Select
              disabled={fetching}
              placeholder="Select an option"
              // @ts-expect-error - TS2769 - No overload matches this call. | TS2345 - Argument of type 'string' is not assignable to parameter of type 'never'.
              value={mappedChoices.find(({value}) => selected.includes(value))}
              options={mappedChoices}
              onChange={(selectedChoice) => {
                if (selectedChoice) {
                  // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'never'.
                  setSelected([selectedChoice.value]);
                } else {
                  setSelected([]);
                }
              }}
            />
          )}
        </div>
      )}
      {showRadioButtons && (
        <div className={css.multipleChoice}>
          <RadioButtons
            name="choices"
            selectedValue={selected[0]}
            onChange={(choice) => {
              // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'never'.
              setSelected([choice]);
            }}
            options={mappedChoices}
            bodyClassName={css.optionGroup}
          />
        </div>
      )}
      {showCheckboxes && (
        <div className={css.optionGroup}>
          {mappedChoices.map(({value, label}) => (
            <label>
              <Checkbox
                disabled={
                  selected.length > 0 &&
                  // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'never'.
                  !selected.includes(value) &&
                  // @ts-expect-error - TS2339 - Property 'multi_select_limit' does not exist on type 'MultipleChoiceQuestion'.
                  selected.length === data.multi_select_limit
                }
                key={value}
                className={css.checkbox}
                // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'never'.
                checked={selected.includes(value)}
                onChange={() => {
                  // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'never'.
                  if (selected.includes(value)) {
                    setSelected(selected.filter((choice) => choice !== value));
                  } else {
                    // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'never'. | TS2322 - Type 'string' is not assignable to type 'never'.
                    setSelected([...selected, value]);
                  }
                }}
              />
              {label}
            </label>
          ))}
        </div>
      )}
      <button
        className={classify({
          [css.submitButton]: allowSubmit,
          [css.disabledButton]: !allowSubmit,
        })}
        // @ts-expect-error - TS2322 - Type '(evt: Event) => void' is not assignable to type 'MouseEventHandler<HTMLButtonElement>'.
        onClick={(evt: Event) => {
          evt.preventDefault();
          if (allowSubmit) {
            // @ts-expect-error - TS2339 - Property 'allow_select_multiple' does not exist on type 'MultipleChoiceQuestion'.
            const saveValue = data.allow_select_multiple
              ? selected
              : selected[0];
            handleNext(saveValue);
          }
        }}
        disabled={!allowSubmit}
      >
        Submit
      </button>
      {/* following pattern of other survey modules */}
      {buttonColor && (
        <style>{`
          .${buttonClass} {
            background-color: ${buttonColor};
            border-color: ${buttonColor};
          }
          .${buttonClass}:hover {color: ${buttonColor};}
        `}</style>
      )}
    </div>
  );
};

export default MultipleChoiceSurveyQuestion;
